import { type } from 'arktype';
import { joinStatusSchema, participantContactResponseSchema, participantResponseSchema } from '../experienceJoins';
import { createPaginatedResponseSchema, paginationSchema } from '../shared/pagination';
import { ageRangeSchema } from '../users';

/** Tope de subtipos por experiencia (documento 03c): como mucho tres a la vez. */
const MAX_SUBTIPOS = 3;

/**
 * Los cinco enumerados propios de "experience" (documento 03a, §5.3). Mismo estilo que
 * `contracts/users`: constante exportada (`as const`) + unión de literales ArkType
 * (`type.enumerated(...)`).
 */

export const EXPERIENCE_DURATION_TYPES = ['ONE_DAY', 'WEEKEND', 'MULTI_DAY'] as const;
export const experienceDurationTypeSchema = type.enumerated(...EXPERIENCE_DURATION_TYPES);
export type ExperienceDurationType = typeof experienceDurationTypeSchema.infer;

export const EXPERIENCE_FORMATS = ['PLAN', 'TALLER', 'ESCAPADA', 'ACAMPADA', 'CASA_RURAL', 'RETIRO', 'VIAJE'] as const;
export const experienceFormatSchema = type.enumerated(...EXPERIENCE_FORMATS);
export type ExperienceFormat = typeof experienceFormatSchema.infer;

export const EXPERIENCE_SUBTYPES = [
	'NETWORKING',
	'VOLUNTARIADO',
	'EVENTO',
	'EXPEDICION',
	'RETO',
	'TEAMBUILDING',
	'CAMPAMENTO',
] as const;
export const experienceSubtypeSchema = type.enumerated(...EXPERIENCE_SUBTYPES);
export type ExperienceSubtype = typeof experienceSubtypeSchema.infer;

export const EXPERIENCE_ACCESS_LABELS = ['OFFICIAL', 'MEMBERS_ONLY', 'COLLABORATOR', 'BRAND', 'PRIVATE'] as const;
export const experienceAccessLabelSchema = type.enumerated(...EXPERIENCE_ACCESS_LABELS);
export type ExperienceAccessLabel = typeof experienceAccessLabelSchema.infer;

export const EXPERIENCE_STATUSES = [
	'DRAFT',
	'PENDING_APPROVAL',
	'PUBLISHED',
	'CANCELLED',
	'FINISHED',
	'TRASH',
] as const;
export const experienceStatusSchema = type.enumerated(...EXPERIENCE_STATUSES);
export type ExperienceStatus = typeof experienceStatusSchema.infer;

/**
 * El saco deliberado (documento 03a, §5.4): todo lo que el asistente recoge y que
 * "experience" no tiene columna propia para guardar. La base lo sigue guardando como
 * JSONB sin forma declarada, pero el contrato le da un tipo explícito — es un punto
 * expreso de la Definition of Done del documento 03a (§14): "existe un tipo declarado
 * para el campo JSON, aunque la base lo siga guardando como JSON". Todos los campos son
 * opcionales porque cada paso del asistente que los recoge también lo es.
 *
 * Documento 03c: `whatsappGroupUrl` SALIÓ de este saco a columna propia de "experience"
 * (`whatsappGroupUrl` TEXT, CIFRADA — ADR-0019): un campo JSON no puede cifrarse
 * selectivamente, así que ya no vive aquí — viaja plano en `createExperienceSchema` y
 * `updateExperienceByOrganizerSchema`, nunca dentro de `intakeMeta`.
 */
export const intakeMetaSchema = type({
	// D-4 del documento 03a, sin resolver: el mensaje del colaborador sobre sus productos
	// sigue siendo texto libre dentro de este saco, sin selector de productos (decisiones.md).
	'collaboratorProductMessage?': 'string',
	// Las condiciones propias de quien organiza (entrega del cliente 18/09/2026,
	// `2-legal/consentimientos-y-firma.md` §5): "Toda experiencia presencial se publica con las
	// condiciones de quien la organiza. Sin ellas no se publica" — la puerta de publicar la
	// exige (código de error `organizerConditionsRequired`, `errors/codes.ts`), pero eso lo
	// decide el servicio de publicación, no este contrato. NO SE VALIDAN: son suyas, las
	// escribe él y responde él — ni longitud mínima, ni formato, ni contenido.
	'condicionesPropias?': 'string',
	// El enlace a un documento propio (p. ej. un PDF con sus condiciones completas), alternativo
	// o complementario al texto libre de `condicionesPropias`. Mismo criterio: no se valida.
	'condicionesPropiasEnlace?': 'string',
	// Distinto del precio de entrada ("priceCents"): lo que el organizador estima que se
	// gasta cada participante por su cuenta (comida, material propio…).
	'estimatedBudgetPerPerson?': 'number >= 0',
	'howToArrive?': 'string',
	'level?': 'string',
	'logistics?': 'string',
	'material?': 'string',
	// El dato más sensible de todo el sistema (documento 03a §5.4): solo viaja redactado,
	// vía `experienceDetailResponseSchema` y `MAPA_VISIBILIDAD_EXPERIENCIA`.
	'meetingPoint?': 'string',
	// Debería enganchar con "experienceSeries" en vez de quedarse como texto suelto —
	// y lo hace: el servicio busca-o-crea la serie a partir de este nombre (documento 03a §7.2).
	'recurringGroupName?': 'string',
}).onUndeclaredKey('delete');

export type IntakeMeta = typeof intakeMetaSchema.infer;

/**
 * `POST /experiences` (documento 03a, §6.1): los campos que recoge el asistente de ocho
 * pasos (documento 03b, §7.1), más `nicheIds` (el catálogo real de nichos, no nombres
 * sueltos — CA-03a.10) e `intakeMeta`. Deliberadamente sin `coordinatorUserId`
 * (sale del token, nunca del cuerpo — RN-4), sin `status` (siempre nace `DRAFT`, decisión
 * 2) y sin `seriesId` (lo resuelve el servicio buscando-o-creando a partir de
 * `intakeMeta.recurringGroupName`, documento 03a §7.2).
 *
 * RN-7 ("un subtipo nunca va solo: siempre formato + subtipo") se cumple por
 * construcción: `format` es un campo obligatorio de este contrato, así que no existe
 * combinación válida donde `subtypes` tenga elementos sin que `format` también los
 * acompañe — no hace falta un `.narrow()` que nunca podría rechazar nada.
 *
 * Documento 03c: `deTemporada` es obligatorio (el paso 6 del asistente obliga a elegir; el
 * `DEFAULT false` de la columna existe solo por las filas ya sembradas, nunca como respuesta
 * válida sin elegir). `whatsappGroupUrl` sale del saco `intakeMeta` a columna propia porque se
 * guarda CIFRADO (ADR-0019) — un campo JSON no puede cifrarse selectivamente, así que viaja
 * aquí, plano, no anidado. Deliberadamente SIN `privateShareToken` (lo genera el servidor,
 * nunca lo elige quien crea) ni `coverAdminUrl` (la pone administración, no el organizador al
 * crear) — los dos entran solo en los schemas de respuesta.
 */
export const createExperienceSchema = type({
	'accessLabel?': experienceAccessLabelSchema,
	'ageSegments?': ageRangeSchema.array(),
	'brandLogoUrl?': 'string',
	'brandName?': 'string',
	// CL-4 (autorizada): nulo es aforo ilimitado; el valor, si llega, nunca puede ser cero.
	'capacity?': 'number.integer >= 1 | null',
	categoryId: 'string.uuid',
	'coverOrganizerUrl?': 'string',
	'description?': 'string',
	deTemporada: 'boolean',
	durationType: experienceDurationTypeSchema,
	// "Fecha límite" en el paso 3 del asistente (documento 03b §7.1): el último día de la
	// experiencia. CL-5 (autorizada) exige que no sea anterior al inicio — ver `.narrow()`.
	'endDate?': 'string.date.iso',
	format: experienceFormatSchema,
	'friendInviteOpen?': 'boolean',
	'intakeMeta?': intakeMetaSchema,
	'locationId?': 'string.uuid',
	'newcomerReservePct?': 'number.integer >= 0',
	'nicheIds?': 'string.uuid[]',
	'priceCents?': 'number.integer >= 0',
	'startDate?': 'string.date.iso',
	'subtypes?': experienceSubtypeSchema.array(),
	title: 'string',
	'whatsappGroupUrl?': 'string',
})
	.onUndeclaredKey('delete')
	.narrow((data, ctx) => {
		if (data.startDate !== undefined && data.endDate !== undefined && data.endDate < data.startDate) {
			return ctx.reject({ message: 'la fecha de fin no puede ser anterior a la de inicio', path: ['endDate'] });
		}
		return true;
	})
	// Documento 03c: como mucho tres subtipos por experiencia.
	.narrow((data, ctx) => {
		if (data.subtypes !== undefined && data.subtypes.length > MAX_SUBTIPOS) {
			return ctx.reject({ message: 'como máximo se pueden elegir tres subtipos', path: ['subtypes'] });
		}
		return true;
	});

export type CreateExperience = typeof createExperienceSchema.infer;

/**
 * `POST /experiences/:id/publish` (decisión 2, decisiones.md): la publicación es un
 * segundo paso explícito, separado de la creación — desviación consciente sobre el PDF,
 * que en su §9 asume que se publica directo. Sin cuerpo propio hoy: el identificador va
 * en la URL. Se declara igualmente como contrato (en vez de dejar el endpoint sin
 * esquema) para que un futuro campo opcional no rompa la convención de "cada operación
 * tiene su contrato".
 */
export const publishExperienceSchema = type({}).onUndeclaredKey('delete');

export type PublishExperience = typeof publishExperienceSchema.infer;

/**
 * `POST /experiences/:id/cancel` (respuestas del cliente 2026-09-17, P-15): «Sí, al centro de
 * mando, con motivo obligatorio. [...] Un plan cancelado desaparece del catálogo, del mapa y
 * del calendario [...] Las plazas se liberan, y quien tenía plaza lo ve en "Mis experiencias"
 * con el motivo». `reason` es obligatorio y no admite cadena vacía (`'string >= 1'`, mismo
 * idioma que `tokenHash` en `contracts/auth`): un motivo en blanco no es un motivo.
 */
export const cancelExperienceSchema = type({
	reason: 'string >= 1',
}).onUndeclaredKey('delete');

export type CancelExperience = typeof cancelExperienceSchema.infer;

/**
 * `PATCH /experiences/:id` (documento 03a, §6.3): solo `ADMIN` (D-16, ya respondida —
 * decisiones.md), con acceso a cualquier campo, incluidos los que P-11 (documento 03c) le
 * bloquea al organizador (`accessLabel`, `categoryId`...). El organizador edita su propia
 * experiencia por una vía distinta y más estrecha: `updateExperienceByOrganizerSchema`, más
 * abajo. Ningún campo es obligatorio aquí: se parchea lo que se envía.
 */
export const updateExperienceSchema = type({
	'accessLabel?': experienceAccessLabelSchema,
	'ageSegments?': ageRangeSchema.array(),
	'capacity?': 'number.integer >= 1 | null',
	'categoryId?': 'string.uuid',
	'description?': 'string',
	// Los tres campos siguientes (respuestas 2026-09-17, P-33: «Los campos nuevos —temporada,
	// enlace del grupo, plantilla— se abren a administración»). `deTemporada`/`whatsappGroupUrl`
	// ya son columnas de "experience" desde el documento 03c; esta tarea solo abre su edición vía
	// PATCH a quien administra (antes no viajaban en ningún contrato de actualización).
	'deTemporada?': 'boolean',
	'durationType?': experienceDurationTypeSchema,
	'endDate?': 'string.date.iso | null',
	'format?': experienceFormatSchema,
	'intakeMeta?': intakeMetaSchema,
	'isExclusive?': 'boolean',
	'locationId?': 'string.uuid | null',
	'newcomerReservePct?': 'number.integer >= 0',
	'nicheIds?': 'string.uuid[]',
	'priceCents?': 'number.integer >= 0 | null',
	// La «plantilla» (P-33) tiene los mismos dos orígenes mutuamente excluyentes que ya declara
	// `invitation` (`templateSeriesId`/`templateId`, `contracts/invitations`): una serie real
	// (`seriesId`, columna ya existente en "experience" desde la iteración 1) o una plantilla
	// guardada a mano (`templateId`). SOLO `seriesId` entra aquí: `templateId` NO tiene columna
	// propia en "experience" (solo en "invitation" y "experienceTemplateNiche") — declararlo
	// aquí lo dejaba validar y descartar en silencio, sin avisar de la pérdida de datos (P-07,
	// hallazgo del cierre de esta tarea, `ExperiencesService.actualizarComoAdmin`). Vuelve a este
	// esquema el día que exista esa columna, con su propia migración — no antes.
	'seriesId?': 'string.uuid',
	'startDate?': 'string.date.iso | null',
	'subtypes?': experienceSubtypeSchema.array(),
	'title?': 'string',
	'whatsappGroupUrl?': 'string',
})
	.onUndeclaredKey('delete')
	.narrow((data, ctx) => {
		if (
			data.startDate !== undefined
			&& data.startDate !== null
			&& data.endDate !== undefined
			&& data.endDate !== null
			&& data.endDate < data.startDate
		) {
			return ctx.reject({ message: 'la fecha de fin no puede ser anterior a la de inicio', path: ['endDate'] });
		}
		return true;
	});

export type UpdateExperience = typeof updateExperienceSchema.infer;

/**
 * `PATCH /experiences/:id` cuando quien edita es el propio organizador (documento 03c, P-11):
 * un subconjunto deliberadamente más pequeño que `updateExperienceSchema` (que sigue siendo
 * solo de administración) — los mismos diez campos que `CAMPOS_EDITABLES_ORGANIZADOR`
 * (`src/common/src/rules/experiences.ts`, `puedeEditarCampo`), más `ageSegments`
 * (respuestas 2026-09-17, P-33: «las franjas de edad se abren a quien organiza»), que no forma
 * parte de esa lista porque `puedeEditarCampo` gobierna columnas simples, no un array con su
 * propia regla de admisibilidad (`puedeCambiarFranjasEdad`, misma fichero). El resto de columnas
 * (`accessLabel`, `categoryId`, `format`, `subtypes`...) quedan bloqueadas para el organizador.
 * `material`, `level`, `meetingPoint`, `howToArrive` y `whatsappGroupUrl` viajan planos aquí,
 * no anidados dentro de `intakeMeta`: el saco JSON es un detalle de almacenamiento, no de la
 * forma de esta petición — el servicio los reubica al escribir. La regla de que el aforo solo
 * puede SUBIR (nunca bajar de los ya confirmados), el aviso al mover la fecha y si las franjas
 * de edad nuevas dejarían fuera a alguien ya confirmado no son forma, son negocio: los decide el
 * servicio con `puedeCambiarPorcentajeReserva`/`puedeCambiarFranjasEdad`/valores reales, no este
 * contrato.
 */
export const updateExperienceByOrganizerSchema = type({
	// Respuestas 2026-09-17, P-33: «Las franjas de edad se abren a quien organiza, con una
	// condición: si ya hay alguien confirmado que se quedaría fuera de la franja nueva, no se
	// cambia». La condición es negocio, no forma — la decide el servicio con
	// `puedeCambiarFranjasEdad` (`rules/experiences.ts`), no este contrato.
	'ageSegments?': ageRangeSchema.array(),
	'capacity?': 'number.integer >= 1 | null',
	'description?': 'string',
	'endDate?': 'string.date.iso | null',
	'howToArrive?': 'string',
	'level?': 'string',
	'material?': 'string',
	'meetingPoint?': 'string',
	'startDate?': 'string.date.iso | null',
	'title?': 'string',
	'whatsappGroupUrl?': 'string',
})
	.onUndeclaredKey('delete')
	.narrow((data, ctx) => {
		if (
			data.startDate !== undefined
			&& data.startDate !== null
			&& data.endDate !== undefined
			&& data.endDate !== null
			&& data.endDate < data.startDate
		) {
			return ctx.reject({ message: 'la fecha de fin no puede ser anterior a la de inicio', path: ['endDate'] });
		}
		return true;
	});

export type UpdateExperienceByOrganizer = typeof updateExperienceByOrganizerSchema.infer;

/**
 * Entero 18-70 que además acepta su representación en cadena (`"25"`): mismo mecanismo que
 * `limitField` (`contracts/shared/pagination.ts`) — Express entrega los query params como
 * string, nunca como número nativo. Eje «edad» del mockup
 * `design-system/ui_kits/app/screens/planes.html`, sección de filtros «Afinamos por lo que
 * vives».
 */
const edadEnRango = type('18 <= number.integer <= 70');
const edadDesdeCadena = type('string.integer.parse').to(edadEnRango);
const edadField = edadEnRango.or(edadDesdeCadena);

/**
 * Entero >= 0 que además acepta su representación en cadena, mismo mecanismo que
 * `edadField`. `0` es "gratis" (mismo mockup, filtro de precio del bloque «Afinamos por lo
 * que vives»).
 */
const precioMaxCentsEnRango = type('number.integer >= 0');
const precioMaxCentsDesdeCadena = type('string.integer.parse').to(precioMaxCentsEnRango);
const precioMaxCentsField = precioMaxCentsEnRango.or(precioMaxCentsDesdeCadena);

/**
 * Booleano que además acepta su representación en cadena (`'true'`/`'false'`): mismo motivo
 * que `edadField`/`limitField` — un query param `soloOficiales=true` llega a Express como la
 * cadena `"true"`, nunca como booleano nativo. A diferencia de `limitField`, ArkType no trae
 * un keyword `string.boolean.parse`, así que la rama de cadena se construye a mano con
 * `.pipe()` sobre la unión de los dos literales posibles.
 */
const soloOficialesDesdeCadena = type("'true' | 'false'").pipe(valor => valor === 'true');
const soloOficialesField = type('boolean').or(soloOficialesDesdeCadena);

/**
 * El eje «cuándo» del mockup `design-system/ui_kits/app/screens/planes.html`, sección de
 * filtros «Afinamos por lo que vives»: mismo molde que el resto de enumerados de este
 * fichero (constante `as const` exportada + unión de literales `type.enumerated`).
 */
export const EXPERIENCE_LIST_QUERY_CUANDO_VALUES = ['ahora', 'semana', 'finde', 'mes'] as const;
export const experienceListQueryCuandoSchema = type.enumerated(...EXPERIENCE_LIST_QUERY_CUANDO_VALUES);
export type ExperienceListQueryCuando = typeof experienceListQueryCuandoSchema.infer;

/**
 * `GET /experiences` (documento 03a, §6.3), ampliado para que el catálogo del mockup
 * `design-system/ui_kits/app/screens/planes.html` (sección de filtros «Afinamos por lo que
 * vives») pueda filtrarse en servidor en vez de traer todo el listado y filtrar en cliente.
 * Compone `paginationSchema` (`cursor?`, `limit`) vía `.merge()` con los seis ejes de
 * filtro, todos opcionales — un listado sin ningún filtro sigue siendo válido, igual que
 * hoy. `.merge()` no conserva el `onUndeclaredKey('delete')` de `paginationSchema` (se
 * resetea a "ignore"), así que hace falta reencadenarlo tras la fusión o una clave huérfana
 * pasaría de largo en vez de descartarse.
 *
 * `soloOficiales` NO equivale a `accessLabel === 'OFFICIAL'`: es la misma pregunta de
 * negocio que resuelve `esExperienciaOficial` (`rules/experiences.ts`, documento 03c, D-5) —
 * `OFFICIAL | COLLABORATOR | BRAND` —, así que quien resuelva este filtro en el backend debe
 * delegar en esa función, nunca reimplementar la lista de etiquetas aquí.
 *
 * `categoryId` es el eje «Tu ADN» (el catálogo `category` tiene seis filas fijas: tierra,
 * agua, aire, nieve, fitness, hobbies). `region` es el eje «Zona», se resuelve contra
 * `location.region` — igual que `experienceZoneSchema.region`, más abajo. `edad` y
 * `precioMaxCents` (`0` = «gratis») son los otros dos filtros del mismo bloque del mockup.
 */
export const experienceListQuerySchema = paginationSchema
	.merge({
		'categoryId?': 'string.uuid',
		'cuando?': experienceListQueryCuandoSchema,
		'edad?': edadField,
		'precioMaxCents?': precioMaxCentsField,
		'region?': 'string',
		'soloOficiales?': soloOficialesField,
	})
	.onUndeclaredKey('delete');

export type ExperienceListQuery = typeof experienceListQuerySchema.infer;

/**
 * La zona aproximada de una localización (documento 02b §8.3, CA-02b.8/CA-02b.9 — hueco 3
 * detectado al cierre de la oleada 2, `docs/estado-ejecucion.md`): solo `region` y `town`.
 * Deliberadamente sin `country`, `lat`, `lng` ni `isSpot` (`contracts/locations`): esos
 * campos no son "zona aproximada", y el punto exacto ya vive, sin cifrar, en
 * `intakeMeta.meetingPoint` — no aquí. Declarada aparte, como `archetypeSchema` en
 * `contracts/users`, para poder marcarla nullable con `.or('null')` dentro del DTO de
 * detalle, y para poder reutilizar exactamente el mismo símbolo en `experienceResponseSchema`
 * (campo `zone`, más abajo) y en `experienceProvinceStatsSchema`.
 */
export const experienceZoneSchema = type({
	region: 'string | null',
	town: 'string | null',
}).onUndeclaredKey('delete');

export type ExperienceZone = typeof experienceZoneSchema.infer;

/**
 * DTO básico (documento 03a, §6.3: `GET /experiences` y `GET /experiences/:id`, ambos con
 * sesión obligatoria). Deliberadamente sin `intakeMeta`: ese campo guarda el dato más
 * sensible del sistema (el punto de encuentro exacto, §5.4) y solo debe viajar redactado
 * a través de `experienceDetailResponseSchema` — devolverlo aquí tal cual repetiría el
 * riesgo que el propio documento 03a señala como fallo conocido ("hoy no se filtra porque
 * el DTO no lo expone").
 */
export const experienceResponseSchema = type({
	accessLabel: experienceAccessLabelSchema,
	'adnHexPrimary?': 'string | null',
	'adnHexSecondary?': 'string | null',
	ageSegments: ageRangeSchema.array(),
	'brandLogoUrl?': 'string | null',
	'brandName?': 'string | null',
	'capacity?': 'number | null',
	categoryId: 'string.uuid',
	'confirmedAt?': 'string.date.iso | null',
	confirmedCount: 'number',
	'coordinatorUserId?': 'string.uuid | null',
	// Documento 03c: administración, siempre gana sobre las demás portadas — `resolverPortada`.
	'coverAdminUrl?': 'string | null',
	'coverOrganizerUrl?': 'string | null',
	createdAt: 'string.date.iso',
	currency: 'string',
	'description?': 'string | null',
	deTemporada: 'boolean',
	durationType: experienceDurationTypeSchema,
	'endDate?': 'string.date.iso | null',
	format: experienceFormatSchema,
	friendInviteOpen: 'boolean',
	id: 'string.uuid',
	isExclusive: 'boolean',
	'locationId?': 'string.uuid | null',
	newcomerReservePct: 'number',
	// Catálogo del mockup (`design-system/ui_kits/app/screens/planes.html`): quién organiza,
	// para pintar la tarjeta sin una segunda petición a `/users/:id`. Opcional porque su
	// ausencia significa "este visor no puede verlo" (mismo criterio que
	// `MAPA_VISIBILIDAD_PERFIL_USUARIO`, ADR-0017), nunca "el organizador no existe" — el
	// mapper del backend decide si lo rellena según quién pregunta.
	'organizerFullName?': 'string | null',
	// Mismo criterio que `organizerFullName`: ausente cuando este visor no tiene permiso para
	// verlo, no cuando el dato no existe.
	'organizerUsername?': 'string | null',
	'priceCents?': 'number | null',
	// El enlace propio del plan privado (documento 03c; auditoría funcional 2026-09-10, B5/H-06):
	// viaja aquí solo en las respuestas que ya pasaron la puerta organizador/ADMIN
	// (`ExperiencesService.obtenerParaGestion`, `GET /experiences/:id`) y en `POST /experiences`,
	// al creador. `GET /experiences` (el listado) nunca lo lleva, aunque comparta este mismo
	// schema — el mapper (`toExperienceResponse`) sencillamente no lo rellena; solo
	// `toExperienceGestionResponse` lo añade. Sigue sin whatsappGroupUrl: es el otro dato
	// sensible de "experience" (cifrado en reposo — ADR-0019) y este es el DTO BÁSICO, sin
	// redacción por visibilidad — igual que ya excluye `intakeMeta` (ver el JSDoc de arriba).
	// Solo viaja a través de `experienceDetailResponseSchema`, ya redactado.
	'privateShareToken?': 'string | null',
	'reviewedAt?': 'string.date.iso | null',
	'seriesId?': 'string.uuid | null',
	'startDate?': 'string.date.iso | null',
	status: experienceStatusSchema,
	subtypes: experienceSubtypeSchema.array(),
	title: 'string',
	updatedAt: 'string.date.iso',
	// Mismo símbolo que usa `experienceDetailResponseSchema` para su propio `viewerJoinStatus`
	// (más abajo): el estado de la participación propia del visitante. Opcional porque su
	// ausencia significa "este visor no tiene ninguna participación con esta experiencia", el
	// mismo criterio de "opcional = sin permiso o sin dato aplicable" que ya usa el detalle
	// (ADR-0017), nunca "el campo no existe en este DTO".
	'viewerJoinStatus?': joinStatusSchema,
	// Mismo símbolo que usa `experienceDetailResponseSchema` para su propio `zone` (incluido el
	// `.or('null')`): la zona aproximada. Opcional porque su ausencia significa "este visor no
	// puede verla" (mismo criterio que `MAPA_VISIBILIDAD_PERFIL_USUARIO`, ADR-0017); cuando el
	// visor sí tiene permiso pero la experiencia no tiene localización asignada, viaja como
	// `null`, no ausente — mismo matiz que ya documenta `experienceDetailResponseSchema`.
	'zone?': experienceZoneSchema.or('null'),
}).onUndeclaredKey('delete');

export type ExperienceResponse = typeof experienceResponseSchema.infer;

/**
 * `GET /me/experiences` (P-14): las dos listas ("organizo"/"voy") de "mis planes", cada una del
 * mismo `experienceResponseSchema` de arriba. Vive en este fichero, no en `contracts/users` (su
 * consumidor natural): moverlo allí reabriría el ciclo entre los dos módulos, porque este
 * fichero ya importa `ageRangeSchema` de `contracts/users` (arriba).
 */
export const misExperienciasResponseSchema = type({
	organizo: experienceResponseSchema.array(),
	voy: experienceResponseSchema.array(),
}).onUndeclaredKey('delete');

export type MisExperienciasResponse = typeof misExperienciasResponseSchema.infer;

/**
 * `GET /experiences/:id/detail` (documento 03a, §6.3: sesión opcional, redactado según
 * quién pregunta). Construido por el backend con `redactarPorVisibilidad` +
 * `MAPA_VISIBILIDAD_EXPERIENCIA` (`src/common/src/rules/experiences.ts`); por eso todo
 * campo salvo `id` es opcional aquí: un campo ausente significa que ese visor no tiene
 * permiso para verlo, nunca que el dato no existe (ADR-0017 — un campo prohibido no
 * viaja, no se pone a `null`). `zone` es además nullable en sí misma (no solo ausente):
 * `locationId` puede ser nulo (un borrador puede no tener zona todavía), y en ese caso el
 * visor con permiso recibe `zone: null`, no un campo ausente — la ausencia queda reservada
 * para "sin permiso", nunca para "sin dato" (mismo criterio que `capacity`/`description`).
 */
export const experienceDetailResponseSchema = type({
	'adnHexPrimary?': 'string | null',
	'adnHexSecondary?': 'string | null',
	// Documento 05b §10.5: si el organizador activó "¿qué puedes aportar?" para esta
	// experiencia. Público a propósito — decide si la pantalla, al obtener plaza, pregunta.
	'askContribution?': 'boolean',
	// "Quién va" (documento 05a «la ficha redactada», fila `attendees`; documento 05b §10.6,
	// «el tablón»): solo para el visor `apuntado` u `organizadorOAdmin`
	// (`MAPA_VISIBILIDAD_EXPERIENCIA`). Nunca teléfono ni redes sociales (CA-05b.10).
	'attendees?': participantResponseSchema.array(),
	'brandLogoUrl?': 'string | null',
	'brandName?': 'string | null',
	'capacity?': 'number | null',
	'categoryId?': 'string.uuid',
	// Las condiciones propias de quien organiza (entrega del cliente 18/09/2026,
	// `2-legal/consentimientos-y-firma.md` §2 y §5): "Cuando el `ExperienceDetailDto` las
	// devuelva, `/unirse` las pinta sola: el campo del front ya existe
	// (`UnirseExperience.condicionesOrganizador`)". Redactadas desde "intakeMeta", mismo
	// tratamiento que `howToArrive`/`meetingPoint` más abajo — quién puede verlas y cómo se
	// extraen de ese saco JSON es trabajo del mapper del backend, no de este contrato.
	'condicionesPropias?': 'string | null',
	'condicionesPropiasEnlace?': 'string | null',
	'confirmedCount?': 'number',
	'coverAdminUrl?': 'string | null',
	'coverOrganizerUrl?': 'string | null',
	'description?': 'string | null',
	'deTemporada?': 'boolean',
	'durationType?': experienceDurationTypeSchema,
	'endDate?': 'string.date.iso | null',
	'format?': experienceFormatSchema,
	'friendInviteOpen?': 'boolean',
	// Redactados desde "intakeMeta" — solo para quien tiene plaza u organiza/administra
	// (visor `apuntado` u `organizadorOAdmin`).
	'howToArrive?': 'string | null',
	id: 'string.uuid',
	'locationId?': 'string.uuid | null',
	'meetingPoint?': 'string | null',
	// Documento 03c: plazas guardadas para quien todavía no ha vivido ninguna experiencia —
	// `plazasReservadas(capacity, reservaEfectivaPct(newcomerReservePct))`, condicionado por
	// `aplicaReservaNovatos(accessLabel)`. Deliberadamente público: viaja también al visor
	// `visitante`, sin sesión ni plaza, porque le dice a cualquiera que hay sitio reservado
	// para él («se han guardado N plazas para quien viene por primera vez»). Ausente —nunca
	// 0— cuando la etiqueta de acceso no reserva (`MEMBERS_ONLY`, `PRIVATE`) o el aforo es
	// nulo (aforo ilimitado: nada que reservar): la ausencia de este campo ya significa algo,
	// mismo criterio que el resto de este DTO redactado.
	'newcomerReserveSeats?': 'number.integer >= 0',
	// P-07 (respuestas del cliente 2026-09-17): «la pantalla deja de decir "Confirmar y pagar".
	// Dice que la plaza queda guardada y que el pago se hace directamente con quien organiza, con
	// su nombre delante» — la ficha de `/unirse/:id` (esta respuesta) necesitaba el nombre de
	// quien organiza y no lo tenía. Misma forma y opcionalidad que `organizerFullName` en
	// `experienceResponseSchema` (el DTO del catálogo), copiada de ahí a propósito, no inventada.
	// Visor mínimo `conCuenta` en `MAPA_VISIBILIDAD_EXPERIENCIA` (`rules/experiences.ts`) — nunca
	// `visitante`, para no filtrar quién organiza a cualquiera que pase por esta ficha pública
	// (P-13: «sin cuenta NO se ve [...] quién organiza»).
	'organizerFullName?': 'string | null',
	// Mismo criterio que `organizerFullName`, ver ese JSDoc.
	'organizerUsername?': 'string | null',
	// "Contacto de participantes" (documento 05a «la ficha redactada»): solo para el visor
	// `organizadorOAdmin` — JAMÁS para `apuntado`, que solo ve `attendees` sin contacto.
	'participantContacts?': participantContactResponseSchema.array(),
	// El enlace propio del plan privado (documento 03c), distinto del enlace de organizador
	// (`invitation`). Mismo nivel de redacción que el resto de campos sensibles: ausente si
	// quien pregunta no tiene permiso, nunca expuesto en el DTO básico.
	'privateShareToken?': 'string | null',
	// Documento 05b §10.3: el aforo se cuenta en voz alta, público para cualquiera (visor
	// `visitante`). Ausente cuando el aforo es nulo (ilimitado: nada que contar).
	'seatsLeft?': 'number.integer >= 0',
	'startDate?': 'string.date.iso | null',
	'title?': 'string',
	// El estado de la participación propia del visitante (documento 05a §5.2), para que la
	// pantalla sepa si ya tiene plaza sin tener que consultar `/experience-joins` aparte.
	// Ausente para quien no tiene ninguna participación con esta experiencia.
	'viewerJoinStatus?': joinStatusSchema,
	'whatsappGroupUrl?': 'string | null',
	'zone?': experienceZoneSchema.or('null'),
}).onUndeclaredKey('delete');

export type ExperienceDetailResponse = typeof experienceDetailResponseSchema.infer;

export const experiencePaginatedResponseSchema = createPaginatedResponseSchema(experienceResponseSchema);

export type ExperiencePaginatedResponse = typeof experiencePaginatedResponseSchema.infer;

/**
 * `GET /experiences` SIN sesión (respuestas del cliente 2026-09-17, P-13, «la miel en los
 * labios», ya citada desde el 27/08 en `docs/handoff/mapa-cableado.md`, decisión 13): «se nota
 * que hay vida, el contenido está oculto, y al tocar redirige». Traducido a este DTO, sin cuenta
 * se ve SOLO: el identificador (para poder tocar y que redirija a pedir cuenta), el tipo/formato
 * del plan, la provincia y el mes — y «cuánta gente va» (un número, no quiénes). **Nada más**: ni
 * título, ni fecha exacta, ni sitio, ni organizador, ni participantes. Un schema propio, NO un
 * `.pick()` de `experienceResponseSchema`: la forma es intencionadamente mínima y no debe heredar
 * un campo nuevo que se añada al DTO completo sin pasar antes por esta puerta más estrecha.
 * `region` es la misma zona aproximada que `experienceZoneSchema.region` (nullable: un plan
 * puede no tener localización asignada todavía); `mes` es el mes de `startDate` en 1-12, que el
 * backend calcula — esta tarea no expone la fecha exacta, ni siquiera el año.
 */
export const experienceCatalogPreviewResponseSchema = type({
	confirmedCount: 'number.integer >= 0',
	format: experienceFormatSchema,
	id: 'string.uuid',
	mes: '1 <= number.integer <= 12',
	region: 'string | null',
}).onUndeclaredKey('delete');

export type ExperienceCatalogPreviewResponse = typeof experienceCatalogPreviewResponseSchema.infer;

export const experienceCatalogPreviewPaginatedResponseSchema = createPaginatedResponseSchema(
	experienceCatalogPreviewResponseSchema,
);

export type ExperienceCatalogPreviewPaginatedResponse = typeof experienceCatalogPreviewPaginatedResponseSchema.infer;

/**
 * Una fila del mapa de España del catálogo (mockup `design-system/ui_kits/app/screens/planes.html`,
 * sección «Dónde está pasando»): una provincia con sus dos contadores. `planes` = cuántas
 * experiencias publicadas hay en esa provincia; `abiertos` = de esas, cuántas siguen admitiendo
 * gente por aforo (aforo nulo, o plazas confirmadas por debajo del aforo) — no hay nada
 * personalizado por quien pregunta, el backend calcula este contador sin recibir ningún usuario.
 * `region` transporta el nombre de la zona tal y como la agrupa el backend.
 */
export const experienceProvinceStatsSchema = type({
	abiertos: 'number',
	planes: 'number',
	region: 'string',
}).onUndeclaredKey('delete');

export type ExperienceProvinceStats = typeof experienceProvinceStatsSchema.infer;

/**
 * `GET /experience-stats/provinces` (mockup `design-system/ui_kits/app/screens/planes.html`,
 * sección «Dónde está pasando»): solo las provincias con al menos una experiencia publicada y
 * no privada, ordenadas alfabéticamente — no es un listado fijo de 50, puede crecer o encogerse
 * con el catálogo. Aun así NO usa `createPaginatedResponseSchema` (sin `nextCursor`, sin
 * `cursor`/`limit`): es un agregado completo que se devuelve de una vez, no una lista paginable.
 */
export const experienceProvinceStatsResponseSchema = type({
	'+': 'delete',
	items: experienceProvinceStatsSchema.array(),
});

export type ExperienceProvinceStatsResponse = typeof experienceProvinceStatsResponseSchema.infer;
